import React, {
   memo,
   useEffect,
   useMemo,
   forwardRef,
   useState,
   useImperativeHandle,
   useCallback,
} from "react";
import { useIntl } from "react-intl";
import { handleInitialSqlValues, putElementsInGridTemplate } from "../GeneralScripts";

import projectConfig from "@projectConfig";
import { Message } from "../../BP2Components";
import { useFormContext } from "../../../context/FormContext/FormContext";
import { isFormInWidget } from "../FormV2Scripts";
import { renderField, checkIfVisible, checkIfRequired, checkIfDisabled } from "./FormTabUtils";
import { markDevFormChanged } from "../../../appRedux/actions/DevForm";
import { DevFormContext } from "./DevFormContext";
import RenderDevSaveButton from "../../RenderDevEditForm/components/RenderDevSaveButton/RenderDevSaveButton";

import "./FormTab.less";
import { useFastActionsContext } from "../../Widgets/InfiniteListWidget/FastActionsContext/FastActionContext";
import { useDispatch } from "react-redux";

const setDeep = (obj, path, value) => {
   if (!path || path.length === 0) return obj;
   if (obj == null) obj = typeof path[0] === "number" ? [] : {};
   const copy = Array.isArray(obj) ? obj.slice() : { ...obj };
   let cur = copy;
   for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      const next = cur[key];
      const container =
         next == null
            ? typeof path[i + 1] === "number"
               ? []
               : {}
            : Array.isArray(next)
              ? next.slice()
              : { ...next };
      cur[key] = container;
      cur = cur[key];
   }
   cur[path[path.length - 1]] = value;
   return copy;
};

const normalizeBoolean10 = (value) => {
   if (typeof value === "boolean") return value ? "1" : "0";
   if (value === 1 || value === 0) return String(value);
   return value;
};

const FormTabComponent = (
   { elements, initialData = {}, areOnlyRequiredFieldsVisible, currentActiveTabKey, tabId, tab },
   ref,
) => {
   //Might be useful during tests
   // elements = [...elements, questionnaireElement];
   const {
      defaultValues,
      formId,
      form,
      formUuid,
      isReadOnly,
      template,
      setChangelogData,
      changelog,
      fieldsInfo,
   } = useFormContext();
   const [alertsFields, setAlertsFields] = useState(new Set());
   const [devLogged, setDevLogged] = useState(window.devLogged ?? false);
   const [devElements, setDevElements] = useState(null);

   const { tabsVisibility, setTabsVisibility } = useFastActionsContext();
   const dispatch = useDispatch();

   const memoElements = useMemo(
      () =>
         elements.map((element) => ({
            ...element,
            position:
               element?.type === "table" || element?.type === "listTable"
                  ? "page_width"
                  : element.position,
         })),
      [elements],
   );

   // Reset dev overrides when the base elements change
   useEffect(() => {
      setDevElements(null);
   }, [memoElements]);

   const activeElements = devElements ?? memoElements;

   const patchDevElement = useCallback(
      (techName, patches) => {
         setDevElements((prev) => {
            const elements = prev ?? memoElements;
            const idx = elements.findIndex((el) => el?.tech_name === techName);
            if (idx === -1) return prev;
            let nextEl = elements[idx];
            patches.forEach(({ path, value }) => {
               nextEl = setDeep(nextEl, path, normalizeBoolean10(value));
            });
            const nextList = elements.slice();
            nextList[idx] = nextEl;
            return nextList;
         });
         dispatch(markDevFormChanged());
      },
      [memoElements, dispatch],
   );

   const intl = useIntl();

   useEffect(() => {
      if (areOnlyRequiredFieldsVisible) {
         const alertFieldsWithMessages = Object.entries(fieldsInfo ?? {})
            .filter(([_, val]) => val && val.length > 0)
            .map(([techname]) => techname);

         setAlertsFields((prev) => {
            const newSet = new Set(prev);
            alertFieldsWithMessages.forEach((techname) => newSet.add(techname));
            return newSet;
         });
      }
   }, [fieldsInfo]);

   const checkIfVisibleLocal = (element) => {
      return checkIfVisible(element, areOnlyRequiredFieldsVisible, alertsFields, form, elements);
   };

   const checkIfRequiredLocal = (element) => {
      return checkIfRequired(element, form, elements, areOnlyRequiredFieldsVisible, alertsFields);
   };

   const checkIfDisabledLocal = (uipEdyt, element) => {
      return checkIfDisabled(uipEdyt, element, isReadOnly, form, elements);
   };

   const showNoRequiredFieldsMessage =
      areOnlyRequiredFieldsVisible && !memoElements.some((elem) => checkIfVisibleLocal(elem));

   if (projectConfig.isFormFlex) {
      const formTab = document.querySelector(".form-tab");
      if (formTab?.offsetWidth <= 800) {
         formTab.classList.add("small-form-tab");
      } else if (formTab?.offsetWidth > 800) {
         formTab.classList.remove("small-form-tab");
      }
   }
   // This prevents from writing the same thing in every case
   const propsForAllFields = (element) => {
      //temporary
      if (typeof initialData === "string") {
         const parsedInitialData = JSON.parse(initialData);
         if (
            parsedInitialData[0] &&
            element.FormLoContent.model === parsedInitialData[0].model &&
            element.FormLoContent.atr === parsedInitialData[0].attribute
         ) {
            element.value = parsedInitialData[0].value;
         }
      } else if (defaultValues && defaultValues[element.tech_name]) {
         element.value = defaultValues[element.tech_name];
      }

      return {
         element,
         form,
         formId,
         isRequired: checkIfRequiredLocal,
         disabled: checkIfDisabledLocal(element.object.UIP_EDYT, element),
         isVisible: checkIfVisibleLocal,
         formUuid: formUuid,
         initialData: initialData,
         setChangelogData: setChangelogData,
         key: element.tech_name,
         changelog: changelog,
      };
   };

   useImperativeHandle(ref, () => ({
      getPropsForAllFields: (specificElement) => {
         return specificElement
            ? propsForAllFields(specificElement)
            : memoElements.map(propsForAllFields);
      },
   }));

   const checkTechNames = (elements) => {
      return !elements.find((element) => element.tech_name === "");
   };

   const controllingTechnames = useMemo(() => {
      const technames = new Set();
      activeElements.forEach((element) => {
         element.showHideFields?.forEach((field) =>
            technames.add(field.techname || field.tech_name),
         );
      });
      return [...technames];
   }, [activeElements]);

   const visibilityKey = controllingTechnames
      .map((techname) => form.getFieldValue(techname))
      .join("|");

   const gridTemplateAreas = useMemo(
      () => putElementsInGridTemplate(activeElements, checkIfVisibleLocal),
      [activeElements, devLogged, visibilityKey, areOnlyRequiredFieldsVisible],
   );

   useEffect(() => {
      if (memoElements) {
         handleInitialSqlValues(memoElements);
      }
   }, []);

   useEffect(() => {
      if (!tab?.name || !areOnlyRequiredFieldsVisible) return;

      setTabsVisibility((prev) => {
         const updatedMap = new Map(prev);
         updatedMap.set(tab.name, !showNoRequiredFieldsMessage);
         return updatedMap;
      });
   }, [showNoRequiredFieldsMessage, tab?.name, areOnlyRequiredFieldsVisible]);

   useEffect(() => {
      const handleDevLoggedChange = () => {
         setDevLogged(window.devLogged ?? false);
      };
      window.addEventListener("devLoggedChange", handleDevLoggedChange);
      return () => {
         window.removeEventListener("devLoggedChange", handleDevLoggedChange);
      };
   }, []);

   return (
      <DevFormContext.Provider value={patchDevElement}>
         <div>
            <div
               className={`form-tab ${isFormInWidget(window?.location?.href) && "form-tab-widget"}`}
               style={{
                  gridTemplateAreas,
                  display: !!template ? "none" : "",
               }}
            >
               {activeElements && checkTechNames(activeElements)
                  ? activeElements.map((element) => renderField(element, propsForAllFields))
                  : Message.error(intl.formatMessage({ id: "settings.form_empty_techName" }))}
            </div>
            <RenderDevSaveButton
               fid={formId}
               activeTabId={currentActiveTabKey}
               elements={activeElements}
            />
         </div>
      </DevFormContext.Provider>
   );
};
const FormTab = memo(forwardRef(FormTabComponent));

export default FormTab;
